/** A drawn map becomes a complete cave: quota, time and tick from depth, and the gate it must pass. */

import { createCave, step } from '../../sim';
import { E, isButterfly } from '../../sim/elements';
import { NO_INPUT } from '../../sim/types';
import { buildCave } from '../build';
import type { CaveColors, CaveDef } from '../types';
import { describeIssues, diamondPotential, reachable, validateCave } from '../validate';
import { caveHint } from './names';
import type { FeatureId } from './plan';
import { slackFor, tickFor, valuesFor } from './plan';
import { routeLength } from './route';

export interface Draft {
  readonly id: string;
  readonly name: string;
  readonly theme: FeatureId | undefined;
  readonly colors: CaveColors;
  readonly map: readonly string[];
  readonly bonus: boolean;
  readonly amoebaTime: number;
  readonly magicWallTime: number;
  /** The share of reachable diamonds to ask for. */
  readonly share: number;
}

/** A cave with fewer reachable diamonds than this is too thin to be fun: draw another. */
const MIN_GEMS = { normal: 12, bonus: 6 };

/** Seconds on top of the route, for the look round at the start. */
const TIME_BASE = 20;
/** Route scans added per diamond: lining up, waiting for things to settle, second thoughts. */
const PER_GEM = 3;
/** Each butterfly counts this many of its nine diamonds towards the quota's base. */
const MOTH_CREDIT = 4;
/** Route scans to blast a butterfly: dig under a boulder, step aside, wait for the gems. */
const BLAST_SCANS = 40;

/**
 * The quota is a share of the diamonds Digby can reach (with some credit for each butterfly);
 * the time is the greedy route at this depth's tick, plus margins, times the depth's slack.
 * Undefined if the cave is too thin.
 */
export function finishCave(draft: Draft, depth: number, seed: number): CaveDef | undefined {
  const tick = tickFor(depth);
  const base: CaveDef = {
    id: draft.id,
    name: draft.name,
    hint: '',
    intermission: draft.bonus,
    colors: draft.colors,
    ...valuesFor(depth),
    ...(draft.amoebaTime > 0 ? { amoebaTime: draft.amoebaTime } : {}),
    ...(draft.magicWallTime > 0 ? { magicWallTime: draft.magicWallTime } : {}),
    levels: [{ seed, diamonds: 1, time: 999, tick }],
    map: draft.map,
  };
  const built = buildCave(base, 1);
  const onMap = diamondPotential(built).reachableOnMap;
  if (onMap < (draft.bonus ? MIN_GEMS.bonus : MIN_GEMS.normal)) return undefined;
  const moths = built.cells.reduce((n, el) => n + (isButterfly(el) ? 1 : 0), 0);
  const quota = Math.max(1, Math.round((onMap + MOTH_CREDIT * moths) * draft.share));
  const route = routeLength(built, Math.min(quota, onMap));
  if (route < 0) return undefined;
  // Diamonds beyond those on the map come from butterflies: time to drop a boulder on each.
  const blasts = Math.ceil(Math.max(0, quota - onMap) / 9);
  const scans = route + PER_GEM * quota + BLAST_SCANS * blasts;
  const time = Math.min(999, Math.ceil(((scans * tick) / 1000 + TIME_BASE) * slackFor(depth)));
  return { ...base, hint: caveHint(draft.theme, quota), levels: [{ seed, diamonds: quota, time, tick }] };
}

/**
 * Seeds the idle birth check runs under: the tests use 1; the others stand in for real play. With
 * Digby idle, only the amoeba and slime draw on the engine's dice, so other caves need one run.
 */
const BIRTH_SEEDS = [1, 2, 3];
/** Scans after the hatch that Digby must survive untouched (the tests ask 40). */
const BIRTH_SCANS = 60;

/**
 * Why this cave may not ship, or undefined if it may: validation must be spotless, every key must
 * be reachable, and an idle Digby must be born and survive.
 */
export function whyNot(def: CaveDef): string | undefined {
  const issues = validateCave(def, 'custom');
  if (issues.length > 0) return describeIssues(def, issues).join('; ');
  const built = buildCave(def, 1);
  const seen = reachable(built);
  for (let i = 0; i < built.cells.length; i++) {
    const el = built.cells[i] ?? E.STEEL;
    if (el >= E.KEY && el < E.KEY + 3 && seen[i] !== 1) return 'a key is out of reach';
  }
  const dice = built.cells.some((el) => el === E.AMOEBA || el === E.SLIME);
  for (const seed of dice ? BIRTH_SEEDS : BIRTH_SEEDS.slice(0, 1)) {
    const s = createCave(built, seed);
    const scans = s.hatchScan + 4 + BIRTH_SCANS;
    for (let i = 0; i < scans && s.digby.alive; i++) step(s, NO_INPUT);
    if (!s.digby.born || !s.digby.alive) return `Digby does not survive his birth (seed ${seed})`;
  }
  return undefined;
}
